/**
 * FFT radix-2 iterativa (sem dependências) usada por:
 *  - redução de ruído espectral (spectral gating / Wiener)
 *  - análise de onset (fluxo espectral)
 * Implementação em ponto flutuante, in-place, com tabelas pré-calculadas.
 */

const cache = new Map<number, { rev: Int32Array; cos: Float32Array; sin: Float32Array }>();

function tables(n: number) {
  let t = cache.get(n);
  if (t) return t;
  const rev = new Int32Array(n);
  const bits = Math.log2(n) | 0;
  for (let i = 0; i < n; i++) {
    let x = i, r = 0;
    for (let b = 0; b < bits; b++) {
      r = (r << 1) | (x & 1);
      x >>= 1;
    }
    rev[i] = r;
  }
  const cos = new Float32Array(n / 2);
  const sin = new Float32Array(n / 2);
  for (let i = 0; i < n / 2; i++) {
    const a = (-2 * Math.PI * i) / n;
    cos[i] = Math.cos(a);
    sin[i] = Math.sin(a);
  }
  t = { rev, cos, sin };
  cache.set(n, t);
  return t;
}

export function isPow2(n: number): boolean {
  return n > 0 && (n & (n - 1)) === 0;
}

export function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
}

/**
 * FFT in-place. `re`/`im` devem ter tamanho potência de 2.
 * inverse = true faz a IFFT (com normalização 1/N).
 */
export function fft(re: Float32Array, im: Float32Array, inverse = false): void {
  const n = re.length;
  if (!isPow2(n)) throw new Error('fft: tamanho deve ser potência de 2');
  const { rev, cos, sin } = tables(n);

  for (let i = 0; i < n; i++) {
    const j = rev[i];
    if (j > i) {
      let t = re[i]; re[i] = re[j]; re[j] = t;
      t = im[i]; im[i] = im[j]; im[j] = t;
    }
  }

  for (let size = 2; size <= n; size <<= 1) {
    const half = size >> 1;
    const step = n / size;
    for (let i = 0; i < n; i += size) {
      for (let j = i, k = 0; j < i + half; j++, k += step) {
        const l = j + half;
        const c = inverse ? cos[k] : cos[k];
        const s = inverse ? -sin[k] : sin[k];
        const tre = re[l] * c - im[l] * s;
        const tim = re[l] * s + im[l] * c;
        re[l] = re[j] - tre;
        im[l] = im[j] - tim;
        re[j] += tre;
        im[j] += tim;
      }
    }
  }

  if (inverse) {
    for (let i = 0; i < n; i++) {
      re[i] /= n;
      im[i] /= n;
    }
  }
}

export interface StftOptions {
  fftSize: number;
  hopSize: number;
  window: Float32Array;
}

export function hannWindow(n: number): Float32Array {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

/**
 * Magnitudes por quadro. Retorna { mags: Float32Array[] , frames }
 */
export function stftMagnitudes(
  input: Float32Array,
  opts: StftOptions
): { mags: Float32Array[]; frames: number; binHz: number } {
  const { fftSize, hopSize, window } = opts;
  const bins = fftSize / 2 + 1;
  const frames = Math.max(1, Math.ceil(Math.max(0, input.length - fftSize) / hopSize) + 1);
  const re = new Float32Array(fftSize);
  const im = new Float32Array(fftSize);
  const mags: Float32Array[] = [];
  for (let f = 0; f < frames; f++) {
    const off = f * hopSize;
    re.fill(0); im.fill(0);
    for (let i = 0; i < fftSize; i++) {
      const s = off + i;
      if (s < input.length) re[i] = input[s] * window[i];
    }
    fft(re, im, false);
    const m = new Float32Array(bins);
    for (let k = 0; k < bins; k++) m[k] = Math.hypot(re[k], im[k]);
    mags.push(m);
  }
  return { mags, frames, binHz: 1 };
}
